import { useEffect, useMemo, useState } from "react";

import { BrokerTypes } from "graphql/__generated__/globalTypes";
// ClosingDocumentViewer.tsx
import PDFRenderer from "components/core/pdf-renderer";
import Modal from "components/layout/modal";
import config from "config";
import { useFormik } from "formik";
import { generateURlData } from "utils";
import { parseUrl } from "utils/helpers";
import { urls as __urlss } from "../../../constants";
import { useAppSelector } from "app/hooks";

interface FileItem {
  name: string;
  url: string;
}

const extractPdfLinks = (htmlContent: string): FileItem[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const links = doc.querySelectorAll('a[href$=".pdf"]');

  return Array.from(links).map((link) => ({
    name: link.textContent?.trim() || "Unknown PDF",
    url: link.getAttribute("href") || "",
  }));
};

const documentTabs = [
  {
    name: "Closing Slip",
    href: "CLOSING_SLIP",
  },
  {
    name: "Placing Slip",
    href: "PLACING_SLIP",
  },
  {
    name: "Supporting Documents",
    href: "SUPPORTING_DOCS",
  },
];

interface SupportingDocsViewProps {
  url: string;
  isDisabled: boolean;
  broker?: string | null;
}

const SupportingDocsView: React.FC<SupportingDocsViewProps> = ({
  url,
  isDisabled,
  broker,
}) => {
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [pdfFiles, setPdfFiles] = useState<FileItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!url || isDisabled) {
      setIsLoading(false);
      return;
    }

    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        const response = await fetch(url);
        const htmlContent = await response.text();
        const extractedFiles = extractPdfLinks(htmlContent);
        setPdfFiles(extractedFiles);
      } catch (error) {
        console.error("Error loading supporting documents:", error);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [url, isDisabled]);

  if (isDisabled) {
    return (
      <div className="flex items-center justify-center h-[800px]">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
            <svg
              className="h-6 w-6 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Supporting Documents Not Available
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Supporting documents are not available for {broker} broker.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[800px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center h-[800px]">
        <div className="text-center text-red-600">
          <p>Error loading documents</p>
        </div>
      </div>
    );
  }

  if (pdfFiles.length === 0) {
    return (
      <div className="flex justify-center items-center flex-col h-[800px]">
        <svg
          className="w-20 h-20 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h1 className="text-sm md:text-xl font-semibold mt-2">
          No Attachments available
        </h1>
        <p className="text-xs md:text-lg font-extralight">
          Try adding some supporting documents
        </p>
      </div>
    );
  }

  return (
    <div className="h-[800px] flex gap-4">
      <div className="w-1/3 border-r pr-4 overflow-y-auto">
        <h3 className="font-semibold mb-4 text-gray-700">
          Available Documents
        </h3>
        <div className="space-y-2">
          {pdfFiles.map((file, index) => (
            <button
              key={index}
              onClick={() => setSelectedPdf(file.url)}
              className={`w-full p-3 rounded-lg border transition-colors text-left ${selectedPdf === file.url
                ? "bg-blue-50 border-blue-300 text-blue-700"
                : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                }`}
            >
              <div className="font-medium text-sm truncate">{file.name}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 h-full">
        {selectedPdf ? (
          <div className="h-full rounded-lg overflow-hidden">
            <iframe
              src={selectedPdf}
              width="100%"
              height="100%"
              className="border-none"
              title="PDF Viewer"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p>Select a document to view</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface ClosingDocumentViewerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  offerId: string;
  offer?: any;
  reinsurerId?: string | null;
}


export const getBaseURL = (broker: BrokerTypes) => {
  switch (broker) {
    case BrokerTypes.Visal:
      return config.asset.visalUri;
    case BrokerTypes.Kek:
      return config.asset.kekUri;
    case BrokerTypes.Irisk:
      return config.asset.backendUri;

    default:
      return config.asset.visalUri;
  }

}

const ClosingDocumentViewer = ({
  open,
  setOpen,
  offerId,
  offer,
  reinsurerId,
}: ClosingDocumentViewerProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const [activeTab, setActiveTab] = useState("CLOSING_SLIP");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const { broker } = useAppSelector((state) => state.offers);
  // const broker = offer?.offer.offer_from;

  const form = useFormik({
    initialValues: {
      lang: "en",
    },
    onSubmit: () => { },
  });

  const isSupportingDocsDisabled = useMemo(() => {
    return broker === BrokerTypes.Kek || broker === BrokerTypes.Irisk;
  }, [broker]);

  const getDocumentUrl = () => {
    const baseParams = {
      offer_id: offerId,
      period_from: offer?.offer_detail?.period_of_insurance_from,
      period_to: offer?.offer_detail?.period_of_insurance_to,
      lang: form.values.lang,
    };

    const closingParams = {
      ...baseParams,
      reinsurer_id: reinsurerId,
    };

    const placeItUri = parseUrl(
      `${config.asset.placeItUri}/generate-supporting-docs/${offer?.offer?.place_it_id}`,
      {
        defaultProtocol: "https",
        forceHttps: true,
      }
    );
    const internalDocsUri = parseUrl(
      `${getBaseURL(broker!)}/generate-broker-supporting-docs/${offer?.offer?.offer_id}`,
      {
        defaultProtocol: "https",
        forceHttps: true,
      }
    );

    const placeItKavaOffers = parseUrl(
      `${config.asset.placeItUri}/api/v1/offer-docs-content/${offer?.offersoffer_id}`,
      {
        defaultProtocol: "https",
        forceHttps: true,
      }
    );

    const urls: { [key: string]: string } = {
      PLACING_SLIP: parseUrl(
        `${getBaseURL(broker || BrokerTypes.Visal)
        }/generate_placing_slip/${generateURlData({
          ...baseParams,
          emp_id: 1,
        })}`,
        { forceHttps: true, defaultProtocol: "https" }
      ),
      CLOSING_SLIP: parseUrl(
        `${__urlss[(broker as BrokerTypes) || BrokerTypes.Visal]
        }/generate_closing_slip/${generateURlData(closingParams)}`,
        { forceHttps: true, defaultProtocol: "https" }
      ),
      SUPPORTING_DOCS:
        broker === BrokerTypes.Placeit
          ? placeItKavaOffers
          : offer?.offer?.offer_from === "PLACEIT"
            ? placeItUri
            : internalDocsUri,
    };

    return urls[activeTab] || "";
  };

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
  };

  const handleClose = () => {
    setIsOpen(false);
    setOpen(false);
  };

  return (
    <Modal
      open={isOpen}
      setOpen={handleClose}
      loading={loading}
      title="Offer Documents"
      description="View offer documents"
      size="6xl"
    >
      <div className="flex flex-col h-full">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-4" aria-label="Tabs">
            {documentTabs.map((tab) => {
              const isActive = activeTab === tab.href;

              return (
                <button
                  key={tab.name}
                  onClick={() => handleTabChange(tab.href)}
                  className={`${isActive
                    ? "border-green-500 text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                  aria-current={isActive ? "page" : undefined}
                  title={undefined}
                >
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="flex-1 p-4">
          {activeTab === "SUPPORTING_DOCS" ? (
            <SupportingDocsView
              url={getDocumentUrl()}
              isDisabled={isSupportingDocsDisabled}
              broker={broker}
            />
          ) : (
            <PDFRenderer url={getDocumentUrl()} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ClosingDocumentViewer;
